import { parseISO } from 'date-fns';
import { useIntl } from 'react-intl';

import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useTableContext } from '@/contexts/table-context';
import { cn } from '@/utils/classnames';

import { useHiresToDate } from './context';

interface HiresAfterRangeChipProps {
  /** Applicants in the range hired after its end date. */
  count: number;
  onTurnOn: () => void;
}

/**
 * The `+N` beside the Hires value on a past range. Off, it offers to count the
 * later hires and turns the switch on; on, it says they are included.
 */
export function HiresAfterRangeChip({
  count,
  onTurnOn,
}: HiresAfterRangeChipProps) {
  const intl = useIntl();
  const { isEligible, isOn, today } = useHiresToDate();
  const { dateRange } = useTableContext();

  if (!isEligible || !dateRange.endDate || count <= 0) return null;

  const values = {
    count: intl.formatNumber(count),
    endDate: intl.formatDate(parseISO(dateRange.endDate), {
      month: 'short',
      day: '2-digit',
    }),
    today: intl.formatDate(today, {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    }),
  };

  const title = isOn
    ? intl.formatMessage(
        {
          defaultMessage: 'Includes {count} hired after {endDate}',
          description:
            'All Campaigns Hires card, Hires to date on: how many of the hires came after the range end date',
        },
        values,
      )
    : intl.formatMessage(
        {
          defaultMessage: '{count} hired after {endDate}',
          description:
            'All Campaigns Hires card, Hires to date off: how many of the range applicants were hired after its end date',
        },
        values,
      );
  const body = isOn
    ? intl.formatMessage(
        {
          defaultMessage: 'Counts hires up to today, {today}.',
          description:
            'All Campaigns Hires card, Hires to date on: hires count up to today',
        },
        values,
      )
    : intl.formatMessage({
        defaultMessage:
          'People sourced in this range who were hired after it ended. Select to turn on Hires to date and count them.',
        description:
          'All Campaigns Hires card, Hires to date off: what the later hires are and that selecting the chip counts them',
      });

  return (
    <Tooltip
      content={
        <div className="space-y-1">
          <p className="font-medium">{title}</p>
          <p>{body}</p>
        </div>
      }
    >
      <button
        type="button"
        className={cn(
          'flex h-5 items-center rounded-full px-1.5 whitespace-nowrap transition-colors',
          isOn
            ? 'bg-fx-neutral-100 cursor-default'
            : 'bg-fx-brand-50 cursor-pointer',
        )}
        aria-label={
          isOn
            ? title
            : intl.formatMessage(
                {
                  defaultMessage:
                    'Count {count} more hires from after {endDate}',
                  description:
                    'Accessible name of the All Campaigns chip that turns on Hires to date',
                },
                values,
              )
        }
        onClick={isOn ? undefined : onTurnOn}
      >
        <Typography
          variant="small2"
          className={isOn ? 'text-fx-neutral-600' : 'text-fx-brand-600'}
        >
          +{values.count}
        </Typography>
      </button>
    </Tooltip>
  );
}
